/* ============================================================
   Cascadia Overland Co. - Summit story section
   assets/summit-story.js
   Pairs with sections/summit-story.liquid and
   assets/summit-story.css.

   Loaded with defer. Initialises every .co-summit root on the
   page, and re-initialises when the theme editor swaps the
   section in (shopify:section:load).
   ============================================================ */
(function () {

  /* ---------- 1. Viewport width without the scrollbar ----------
     Feeds the full-bleed breakout in the CSS. Using clientWidth
     instead of 100vw stops Windows browsers from gaining a
     horizontal scrollbar. Page-level, so it runs once.        */
  function setVW() {
    document.documentElement.style.setProperty(
      '--co-vw', document.documentElement.clientWidth + 'px'
    );
  }
  setVW();
  window.addEventListener('resize', setVW);
  window.addEventListener('orientationchange', setVW);

  /* Height of a fixed or sticky header, so anchor jumps and
     programmatic scrolls do not land under it.                */
  function stickyOffset() {
    var pad = 16;
    var candidates = document.querySelectorAll('header, .header, #shopify-section-header, sticky-header');
    for (var i = 0; i < candidates.length; i++) {
      var pos = window.getComputedStyle(candidates[i]).position;
      if (pos === 'fixed' || pos === 'sticky') {
        return candidates[i].getBoundingClientRect().height + pad;
      }
    }
    return pad;
  }

  function scrollToEl(target) {
    var y = target.getBoundingClientRect().top + window.pageYOffset - stickyOffset();
    window.scrollTo({
      top: y,
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
    });
  }

  function init(root) {
    if (!root || root.dataset.coInit === '1') return;
    root.dataset.coInit = '1';

    /* ---------- 2. Scroll to the Configure Your Camper block ----------
       Looks for the heading by its text, then falls back to the
       add-to-cart form, then to the main product section. Change
       the data-co-configure-text attribute on the root if that
       heading is ever renamed.                                 */
    var CONFIG_TEXT = (root.getAttribute('data-co-configure-text') || 'configure your camper').toLowerCase();

    function findConfigureTarget() {
      var heads = document.querySelectorAll('h1,h2,h3,h4,h5,h6,legend');
      for (var i = 0; i < heads.length; i++) {
        if (root.contains(heads[i])) continue;
        if (heads[i].textContent.trim().toLowerCase().indexOf(CONFIG_TEXT) === 0) {
          return heads[i];
        }
      }
      return document.querySelector('form[action*="/cart/add"]')
          || document.querySelector('#MainProduct, .product__info-wrapper, product-info');
    }

    root.addEventListener('click', function (e) {
      var trigger = e.target.closest('[data-co-configure]');
      if (!trigger) return;
      var target = findConfigureTarget();
      if (!target) return;
      e.preventDefault();
      scrollToEl(target);
    });

    /* ---------- 2a. In-page anchors ----------
       Any link to #summit-cabover, or any other id, scrolls
       smoothly and clears a sticky header. Links from outside
       this section still work on their own via the
       scroll-margin-top rule in the CSS.                       */
    root.addEventListener('click', function (e) {
      var link = e.target.closest('a[href^="#"]');
      if (!link || link.hasAttribute('data-co-configure')) return;
      var id = link.getAttribute('href').slice(1);
      if (!id) return;
      var target = document.getElementById(id);
      if (!target) return;
      e.preventDefault();
      scrollToEl(target);
      if (history.replaceState) history.replaceState(null, '', '#' + id);
    });

    /* ---------- 2b. Walkthrough playlist ----------
       Swaps the thumbnail facade for the real iframe on click. */
    var ytBtn = root.querySelector('.co-yt');

    if (ytBtn) {
      var ytVideo = ytBtn.getAttribute('data-yt-video');
      var ytList  = ytBtn.getAttribute('data-yt-playlist');

      /* Thumbnail fallback.
         maxresdefault.jpg only exists when a video was uploaded
         in HD or given a custom HD thumbnail. hqdefault.jpg
         always exists. Because this script runs after the image
         has already started loading, checking naturalWidth is
         required: a late-attached error listener misses a
         failure that already happened, which is what left the
         thumbnail blank.                                       */
      var thumb = ytBtn.querySelector('img');
      if (thumb) {
        var swapThumb = function () {
          if (thumb.dataset.coFallback) return;
          thumb.dataset.coFallback = '1';
          thumb.src = 'https://i.ytimg.com/vi/' + ytVideo + '/hqdefault.jpg';
        };
        thumb.addEventListener('error', swapThumb);
        if (thumb.complete && thumb.naturalWidth === 0) swapThumb();
      }

      /* Warm the connection on hover so playback starts faster */
      var warmed = false;
      function warm() {
        if (warmed) return;
        warmed = true;
        ['https://www.youtube-nocookie.com', 'https://www.google.com'].forEach(function (href) {
          var l = document.createElement('link');
          l.rel = 'preconnect';
          l.href = href;
          document.head.appendChild(l);
        });
      }
      ytBtn.addEventListener('mouseenter', warm, { once: true });
      ytBtn.addEventListener('focus', warm, { once: true });

      ytBtn.addEventListener('click', function () {
        if (ytBtn.classList.contains('is-live')) return;

        var frame = document.createElement('iframe');
        frame.src = 'https://www.youtube-nocookie.com/embed/' + ytVideo +
                    '?list=' + ytList +
                    '&autoplay=1&rel=0&modestbranding=1&playsinline=1';
        frame.title = 'Summit camper walkthrough playlist';
        frame.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
        frame.setAttribute('allowfullscreen', '');
        frame.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin');

        ytBtn.innerHTML = '';
        ytBtn.appendChild(frame);
        ytBtn.classList.add('is-live');
        ytBtn.style.cursor = 'default';
        ytBtn.removeAttribute('aria-label');
      });
    }

    /* ---------- 3. Scroll reveal ---------- */
    var items = root.querySelectorAll('[data-reveal]');
    if (!('IntersectionObserver' in window) || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      items.forEach(function (el) { el.classList.add('is-in'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });
    items.forEach(function (el) { io.observe(el); });
  }

  function boot(scope) {
    var host = scope || document;
    if (host.classList && host.classList.contains('co-summit')) init(host);
    host.querySelectorAll('.co-summit').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(); });
  } else {
    boot();
  }

  /* Theme editor: section re-rendered, so init the new copy */
  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });

})();
